<script lang="ts" setup>
// ================================
// IMPORTS
// ================================

// Vue
import { onMounted, onBeforeUnmount, computed, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { useEventBus } from "@vueuse/core"
import { useAuthStore } from "@core/stores/auth.store"
import { redirectionVersSources } from "@modules/espace-entreprise/components/company/billing/billingSourceCriteres"

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Espace entreprise",
            breadcrumb: {
                accueil: "Accueil",
                utilisateur: "Utilisateur",
                entreprise: "Espace entreprise",
            },
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
// (aucune prop, aucun emit)

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const bus = useEventBus<string>("section-scroll")
const authStore = useAuthStore()

// ================================
// COMPUTED
// ================================

/**
 * Fil d'Ariane pour le header
 */
const getBreadcrumb = computed(() => {
    return [
        { icon: "pi pi-home", label: t("breadcrumb.accueil"), route: "/" },
        {
            icon: "pi pi-user",
            label: t("breadcrumb.utilisateur"),
        },
        { icon: "pi pi-building", label: t("breadcrumb.entreprise") },
    ]
})

/**
 * Affiche la section Abonnements si l'utilisateur est employé avec droits d'écriture ou supérieurs
 */
const getShouldDisplaySubscriptions = computed(
    () => authStore.isEmployeWriteOrHigher
)

/**
 * Affiche la section Membres si l'utilisateur est employé avec droits d'écriture ou supérieurs
 */
const getShouldDisplayMembers = computed(() => authStore.isEmployeWriteOrHigher)

/**
 * Affiche la section Invités si l'utilisateur est client Homeys avec droits d'écriture ou supérieurs
 */
const getShouldDisplayClients = computed(
    () => authStore.isClientHomeys && authStore.isEmployeWriteOrHigher
)

/**
 * Autorisations pour la section Membres (déléguées au parent)
 */
const getCanAddMember = computed(
    () =>
        authStore.isCompanyAdmin &&
        (authStore.isClientHomeys || authStore.isUc2Pro)
)
const getCanChangeRole = computed(() => authStore.isCompanyAdmin)
const getCanDeleteUser = computed(() => authStore.isCompanyAdmin)
const getCanShareBuilding = computed(() => authStore.isCompanyAdmin)

// ================================
// BUSINESS LOGIC
// ================================

/**
 * Fonction pour le défilement vers la section cible
 * @param section - l'id de la section à atteindre avec le scroll
 * @returns void
 */
function scrollToSection(section: string): void {
    const targetSection = document.getElementById(section)
    if (targetSection) {
        targetSection.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center",
        })
    }
}

// ================================
// STATE
// ================================

/**
 * Écoute de l'event bus pour le scroll vers une section
 */
const unsubscribe = bus.on((section: string) => {
    scrollToSection(section)
})

// ================================
// LIFECYCLE
// ================================

/**
 * Scroll vers la section au chargement si hash présent dans l'URL
 */
onMounted(() => {
    const hash = route.hash.replace("#", "")
    if (hash) {
        // Petit délai pour s'assurer que les composants sont rendus
        setTimeout(() => {
            scrollToSection(hash)
        }, 100)
    }
})

/**
 * Lien vers la liste filtrée (`?si_supprimee=libere`, courriels d'alerte de
 * plafond) suivi sans quitter la page : la liste est une page (demande du
 * 16/10), on y mène avec la même query. À l'arrivée, la garde de la route
 * fait déjà cette redirection.
 */
watch(
    () => route.query,
    (query) => {
        const sources = redirectionVersSources(query)
        if (sources) void router.replace(sources)
    }
)

/**
 * Quand le hash change (ex. clic sur un lien du sidebar sans quitter la page),
 * scroller vers la section correspondante.
 */
watch(
    () => route.hash,
    (newHash) => {
        const hash = newHash.replace("#", "")
        if (hash) {
            setTimeout(() => scrollToSection(hash), 100)
        }
    }
)

/**
 * Désabonnement de l'event bus au démontage
 */
onBeforeUnmount(() => {
    unsubscribe()
})
</script>

<template>
    <div class="page page-entreprise">
        <!-- ================================ -->
        <!-- HEADER -->
        <!-- ================================ -->
        <HeaderPage :title="t('title')" :breadcrumb="getBreadcrumb" />

        <!-- ================================ -->
        <!-- SECTION GENERAL INFO -->
        <!-- ================================ -->
        <CompanyGeneralInfo id="generalInfo" class="mt-6" />

        <!-- ================================ -->
        <!-- SECTION SUBSCRIPTIONS -->
        <!-- ================================ -->
        <CompanySubscriptions
            v-if="getShouldDisplaySubscriptions"
            id="subscriptions"
            class="mt-6"
        />

        <!-- ================================ -->
        <!-- SECTION MEMBERS -->
        <!-- ================================ -->
        <CompanyMembers
            v-if="getShouldDisplayMembers"
            id="members"
            class="mt-6"
            :can-add-member="getCanAddMember"
            :can-change-role="getCanChangeRole"
            :can-delete-user="getCanDeleteUser"
            :can-share-building="getCanShareBuilding"
        />

        <CompanyClients
            v-if="getShouldDisplayClients"
            id="invites"
            class="mt-6"
        />

        <!-- ================================ -->
        <!-- SECTION INVOICES -->
        <!-- ================================ -->
        <InvoiceCard
            v-if="authStore.isClientHomeys && authStore.isCompanyOwner"
            id="invoices"
            class="mt-6"
        />

        <!-- ================================ -->
        <!-- SECTION PROFILES -->
        <!-- ================================ -->
        <UsageProfileListCard
            v-if="authStore.isClientHomeys && authStore.isEmployeWriteOrHigher"
            id="profile"
            class="mt-6"
        />
    </div>
</template>
